import React from "react";
import { useTranslation } from "react-i18next";
import {
    StyledModal,
    StyledLabel,
    StyledTextInput,
} from "@aureskonnect/react-ui";
import { useSnapshot } from "valtio";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { AvForm, AvGroup } from "availity-reactstrap-validation";
import { store as useStore } from "@components/VerticalLayout/store";
import { capitalize } from "../../../../helpers/general";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { useFetchGlobal } from "../../../../api/hooks";
import { showSuccessToast } from "@components/Common/SuccessToast";
import ErrorToast from "@components/Common/ErrorTost";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { duplicateProject, generateDuplicateName } from "./api";

export function ConfirmationModalCopied({
    isConfirmationModalCopied,
    setIsConfirmationModalCopied,
    setIsDataUpdated,
    selectedRows,
}: {
    isConfirmationModalCopied: boolean;
    setIsConfirmationModalCopied: (value: boolean) => void;
    setIsDataUpdated: (value: boolean | number) => void;
    selectedRows: Record<string, any>[];
}) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const { userID, franchiseID, operatorID, oneShop, shopID } = useSnapshot(
        useStore
    );
    const [
        isValidateButtonDisabled,
        setIsValidateButtonDisabled,
    ] = React.useState(false);
    const [duplicateName, setDuplicateName] = React.useState(() =>
        generateDuplicateName(selectedRows[0]?.[t("Designation")] ?? "", t)
    );

    async function copie() {
        try {
            const result = await duplicateProject(fetchGlobal, {
                userID,
                franchiseID,
                operatorID,
                oneShop,
                shopID,
                row: selectedRows[0],
                t,
                designation: duplicateName,
            });

            if (!result.error) {
                showSuccessToast(
                    `${t("Project have been successfully duplicated")!}`
                );
                setIsConfirmationModalCopied(!isConfirmationModalCopied);
                setIsDataUpdated(true);
            } else {
                throw new Error("Duplication error");
            }
        } catch {
            ErrorToast(`${t("There's an error")!}`);
        }
    }

    return (
        <StyledModal toggle={true} isOpen={isConfirmationModalCopied!} centered>
            <ModalHeader
                className="text-capitalize"
                toggle={() => {
                    setIsConfirmationModalCopied(!isConfirmationModalCopied);
                    setIsDataUpdated(true);
                }}
            >
                <span>{capitalize(t("Alert"))}</span>
            </ModalHeader>
            <ModalBody>
                <StyledLabel className="pl-3 m-0">
                    {`${t(
                        "Do you want to confirm the duplication of the project"
                    )} ''${selectedRows[0][t("Designation")]}''`}
                </StyledLabel>
                <AvForm className="px-3 mt-3">
                    <AvGroup>
                        <StyledLabel htmlFor="duplicate-project-name">
                            {t("New designation")}
                        </StyledLabel>
                        <StyledTextInput
                            id="duplicate-project-name"
                            name="duplicateName"
                            type="text"
                            value={duplicateName}
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                            ) => setDuplicateName(e.target.value)}
                            autoFocus
                        />
                    </AvGroup>
                </AvForm>
            </ModalBody>
            <ModalFooter className="border-top-0">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    className="cmn_btn_cancelAlert"
                    onClick={() => {
                        setIsConfirmationModalCopied(
                            !isConfirmationModalCopied
                        );
                        setIsDataUpdated(true);
                    }}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    disabled={
                        isValidateButtonDisabled || duplicateName.trim() === ""
                    }
                    className="mr-2 cmn_btn_validateAlert"
                    variant="primary"
                    onClick={() => {
                        setIsValidateButtonDisabled(true);
                        copie();
                        setTimeout(() => {
                            setIsValidateButtonDisabled(false);
                        }, 1000);
                    }}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
